import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  Flex,
  Text,
  Button,
  Box,
  Heading,
  Link,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { DownloadIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { FaUser, FaFileSignature } from "react-icons/fa";

const Assessments = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [conditionRequirements, setConditionRequirements] = useState("");
  const [consentGranted, setConsentGranted] = useState(false);
  const [consentFormUrl, setConsentFormUrl] = useState("");
  const [completedConsents, setCompletedConsents] = useState([]);
  // Reference modal for consent form
  const modalRef = useRef();
  const consentFormRef = useRef();

  const UUID = localStorage.getItem("UUID");
  const HuserID = localStorage.getItem("HuserID");
  const API_BASE_URL = "https://qaapirgeneng.digitalfields.co.za/ws1.cfc";

  // User feedback prompts
  const toast = useToast();
  const [loading, setLoading] = useState(true);

  // GET request getAssessmentsForCandidates for fetching list of assessments
  const getAssessments = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE_URL}?method=getAssessmentsForCandidates&huserID=${HuserID}&uuid=${UUID}`,
        {
          method: "GET",
        }
      );

      const data = await response.json();
      setAssessments(data[0].assessments); // This now contains supplier objects
      // If conditionRequirements text contains "consent" then display Download Consent Form button + My Profile button
      if (!!data[0].conditionRequirements.includes("consent")) {
        setConditionRequirements(data[0].conditionRequirements);
        setConsentGranted(false);
        let HconsentID = data[0].outstandingConsents[0].HconsentID;
        setConsentFormUrl(
          `https://qaapirgeneng.digitalfields.co.za/htmlTemplates/genconsent.cfm?thisHuserID=${HuserID}&uuid=${UUID}&thisHconsentID=${HconsentID}`
        );
      } else if (!!data[0].conditionRequirements.includes("profile")) {
        setConditionRequirements(data[0].conditionRequirements);
        // Check if there are completed consents - if so, allow download
        if (data[0].completedConsents && data[0].completedConsents.length > 0) {
          setConsentGranted(true);
        } else {
          setConsentGranted(false);
        }
        if (data[0].completedConsents && data[0].completedConsents.length > 0) {
          setConsentGranted(true);
        } else {
          setConsentGranted(false);
        }
      } else {
        setConsentGranted(true);
        setConsentFormUrl(null);
      }
      // Store completed consents
      if (data[0].completedConsents) {
        setCompletedConsents(data[0].completedConsents);
      }

    } catch (error) {
      toast({
        title: "Error fetching assessments.",
        description: "",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to handle consent form download
  const handleDownloadConsentForm = async () => {
    try {
      if (!completedConsents || completedConsents.length === 0) {
        throw new Error("No completed consents found");
      }

      const logFileUploadID = completedConsents[0].logFileUploadID;

      const response = await fetch(
        `${API_BASE_URL}?method=downloadConsentDocument&huserID=${HuserID}&uuid=${UUID}&logFileUploadID=${logFileUploadID}`,
        {
          method: "GET",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to download consent form");
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = url;
      link.download = "consent_form.pdf"; // Name of the downloaded file

      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading consent form:", error);
      toast({
        title: "Download Failed",
        description:
          error.message || "There was an error downloading the consent form.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openConsentForm = (url) => {
    // Display modal
    modalRef.current.style.display = "block";
    // Load CFM file
    consentFormRef.current.src = url;
  };

  const closeModal = () => {
    modalRef.current.style.display = "none";
    consentFormRef.current.src = "";
  };

  const closeModalOnSubmission = () => {
    if (!consentFormRef) return;

    const handleLoad = () => {
      try {
        // Check if URL contains success parameters or changed to success page
        const iframeUrl = consentFormRef.contentWindow.location.href;

        // Adjust these conditions based on your CFM form's behavior
        if (
          iframeUrl.includes("success=true") ||
          iframeUrl.includes("submitted=1") ||
          iframeUrl.endsWith("/success") ||
          iframeUrl.includes("thank-you")
        ) {
          closeModal();
        }
      } catch (error) {
        // Cross-origin restrictions might prevent URL access
        console.log("Cannot access iframe URL due to CORS");
      }
    };

    if (consentFormRef.current) {
      consentFormRef.current.addEventListener("load", handleLoad);

      return () => {
        consentFormRef.current.removeEventListener("load", handleLoad);
      };
    }
  };

  useEffect(() => {
    getAssessments();

    closeModalOnSubmission();

    const handleMessage = (event) => {
      if (event.data === "FORM_SUBMITTED") {
        // Show success toast
        toast({
          title: "Consent Form Submitted",
          description: "Your consent form has been submitted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Delay closing the modal
        setTimeout(() => {
          closeModal();
          getAssessments(); // Refresh the data
        }, 2000); // 2 second delay
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [toast]); // Add toast to dependencies

  // Take user to the profile update page
  const viewProfile = useCallback(() => {
    navigate("/profile");
  }, []);

  // Loading spinner
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="30vh"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box p={8} position="relative">
      <Heading mb={5}>Assessments</Heading>

      {/* Consent Form modal */}
      <Box
        ref={modalRef}
        display="none"
        position="absolute"
        zIndex="1000"
        top={{ base: "0", lg: "5vw" }}
        left={{ base: "0", lg: "12vw", xl: "19vw" }}
        bottom="-50vh"
        right={{ base: "0", lg: "12vw", xl: "19vw" }}
        bg="rgba(53, 51, 43, 0.61)}"
      >
        <Flex direction="column" h="100%" className="modal-popup" p="3">
          <Flex justifyContent="end" mb="4">
            <Button
              bg="none"
              _hover={{
                background: "none",
              }}
              color="white"
              size="lg"
              fontWeight="bold"
              onClick={closeModal}
            >
              ×
            </Button>
          </Flex>
          <Box h="100%" className="modal-content">
            <iframe
              ref={consentFormRef}
              style={{
                width: "100%",
                height: "100%",
                border: "2px solid black",
              }}
            ></iframe>
          </Box>
        </Flex>
      </Box>

      {/* Text display for condition requirements */}
      <Text fontSize="small" fontWeight="bold" color="red" mt="10">
        {conditionRequirements}
      </Text>
      <Flex my="4" zIndex="0">
        {/* View User Profile */}
        {conditionRequirements.includes("profile") && (
          <Button
            variant="outline-rounded"
            leftIcon={<FaUser />}
            onClick={viewProfile}
            w="30%"
            mr="4"
          >
            My Profile
          </Button>
        )}

        {/* Sign Consent Form */}
        {consentFormUrl && (
          <Button
            variant="outline-rounded"
            leftIcon={<FaFileSignature />}
            onClick={() => {
              openConsentForm(consentFormUrl);
            }}
            w="30%"
            mr="4"
          >
            Sign Consent Form
          </Button>
        )}

        {/* Download signed Consent Form */}
        {consentGranted && (
          <Button
            variant="outline-rounded"
            leftIcon={<DownloadIcon />}
            onClick={handleDownloadConsentForm}
            w="30%"
          >
            Download Consent Form
          </Button>
        )}
      </Flex>

      {/* Assessments for Candidates - Listing Table */}
      <List>
        {/* Table Header section */}
        <ListItem border="2px" borderColor="gray.600" key="0">
          <Flex>
            <Text
              p="2"
              w="25%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Assessment Name
            </Text>
            <Text
              p="2"
              w="25%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Assessment Status
            </Text>
            <Text
              p="2"
              w="25%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Booking Date/Time
            </Text>
            <Text
              p="2"
              w="25%"
              fontWeight="bold"
              bg="gray.50"
              border="1px solid #e7e7e7"
            >
              Result Date
            </Text>
          </Flex>
        </ListItem>
        
        {/* Table Body section - Now grouped by supplier */}
        {assessments.map((supplier, supplierIndex) => (
          <Box key={supplierIndex}>
            {/* Supplier Header */}
            <ListItem
              borderBottom="1px"
              borderLeft="2px"
              borderRight="2px"
              borderColor="gray.600"
              bg="blue.50"
            >
              <Flex justify="space-between" alignItems="center">
                <Text p="2" w="25%" fontWeight="bold">
                  {supplier.supplierName}
                </Text>
                {supplier.externalLink && (
                  <Flex p="2" w="25%" alignItems="center" gap={2}>
                    <Text fontSize="sm" color="blue.600">
                      Start assessment here →
                    </Text>
                    <Link href={supplier.externalLink} isExternal>
                      <ExternalLinkIcon boxSize={6} color="blue.500" />
                    </Link>
                  </Flex>
                )}
                <Text p="2" w="25%">
                  {/* Empty space for alignment */}
                </Text>
                <Text p="2" w="25%">
                  {/* Empty space for alignment */}
                </Text>
              </Flex>
            </ListItem>
            
            {/* Assessments under this supplier */}
            {supplier.assessmentsArray.map((assessment, assessmentIndex) => (
              <ListItem
                key={`${supplierIndex}-${assessmentIndex}`}
                borderBottom="1px"
                borderLeft="2px"
                borderRight="2px"
                borderColor="gray.600"
                ml="20px" // Indent the assessments
              >
                <Flex justify="space-between">
                  <Text p="2" w="25%">
                    {assessment.assessmentName}
                  </Text>
                  <Text p="2" w="25%">
                    {assessment.assessmentStatus}
                  </Text>
                  <Text p="2" w="25%">
                    {assessment.bookingDateTime}
                  </Text>
                  <Text p="2" w="25%">
                    {assessment.resultdate}
                  </Text>
                </Flex>
              </ListItem>
            ))}
          </Box>
        ))}
      </List>
    </Box>
  );
};

export default Assessments;
